import type { JsonValue } from "../../common/schema/ir";
import { CARD_BRANDS, eanDigit, IBAN_COUNTRIES, ibanCheckDigits, luhnDigit } from "./checksums";
import type { Locale } from "./locales";
import type { Rng } from "./seed";
import { COLOUR_NAMES, COMPANY_STEMS, CURRENCIES, DEPARTMENTS, FILE_EXTENSIONS, HTTP_METHODS, HTTP_STATUSES, JOB_TITLES, LANGUAGE_TAGS, LOREM, MAIL_DOMAINS, MIME_TYPES, PRODUCT_ADJECTIVES, PRODUCT_NOUNS, RESERVED_DOMAINS, STATUSES, TIMEZONES, USER_AGENTS } from "./words";

// What a value can be, which is the only thing that decides whether a name is allowed to have its way: a column
// called `price` under `{"type": "string"}` is a price written as a string, and one called `email` under
// `{"type": "number"}` is a name that means nothing here and is dropped for the type.
export type Produces = "string" | "integer" | "number" | "boolean";

export interface Field {
  produces: Produces;
  generate: (rng: Rng, locale: Locale) => JsonValue;
}

export type FieldId = keyof typeof TABLE;

export function fieldNamed(id: string): Field | undefined {
  return (FIELDS as Record<string, Field>)[id];
}

// Every kind a property name or a schema `format` can land on. Each is a plain function of the stream and the locale,
// so a row is reproducible by construction and nothing here reads a clock, a global or the network.
const TABLE = {
  firstName: { produces: "string", generate: (rng, locale) => rng.pick(locale.givenNames) },
  lastName: { produces: "string", generate: (rng, locale) => rng.pick(locale.surnames) },
  fullName: { produces: "string", generate: fullName },
  username: {
    produces: "string",
    generate: (rng, locale) => `${handle(rng, locale)}${rng.chance(0.4) ? rng.between(2, 99) : ""}`,
  },
  email: {
    produces: "string",
    generate: (rng, locale) => `${handle(rng, locale)}${rng.between(1, 999)}@${rng.pick(MAIL_DOMAINS)}`,
  },
  phone: { produces: "string", generate: (rng, locale) => fromPattern(rng, rng.pick(locale.phones)) },
  jobTitle: { produces: "string", generate: (rng) => rng.pick(JOB_TITLES) },
  department: { produces: "string", generate: (rng) => rng.pick(DEPARTMENTS) },
  gender: { produces: "string", generate: (rng) => rng.pick(["female", "male", "non-binary", "prefer not to say"]) },
  age: { produces: "integer", generate: (rng) => rng.between(18, 79) },
  birthDate: { produces: "string", generate: (rng) => isoDate(bornAt(rng)) },
  birthDateTime: { produces: "string", generate: (rng) => isoDateTime(bornAt(rng)) },

  street: { produces: "string", generate: address },
  city: { produces: "string", generate: (rng, locale) => rng.pick(locale.cities) },
  postcode: {
    produces: "string",
    generate: (rng, locale) => fromPattern(rng, rng.pick(locale.postcodes), locale.postcodeLetters),
  },
  region: { produces: "string", generate: (rng, locale) => rng.pick(locale.regions) },
  country: { produces: "string", generate: (_rng, locale) => locale.country },
  countryCode: { produces: "string", generate: (_rng, locale) => locale.countryCode },
  latitude: { produces: "number", generate: (rng) => rng.float(-90, 90, 6) },
  longitude: { produces: "number", generate: (rng) => rng.float(-180, 180, 6) },
  timezone: { produces: "string", generate: (rng) => rng.pick(TIMEZONES) },

  company: {
    produces: "string",
    generate: (rng, locale) => `${rng.pick(COMPANY_STEMS)} ${rng.pick(locale.companySuffixes)}`,
  },
  product: {
    produces: "string",
    generate: (rng) => `${rng.pick(PRODUCT_ADJECTIVES)} ${rng.pick(PRODUCT_NOUNS)}`,
  },
  // A currency without decimal places is not a currency rounded to zero of them: yen has no subunit at all.
  price: {
    produces: "number",
    generate: (rng, locale) => locale.currency === "JPY" ? rng.between(100, 99999) : rng.float(1, 999, 2),
  },
  currency: { produces: "string", generate: (rng, locale) => rng.chance(0.6) ? locale.currency : rng.pick(CURRENCIES) },
  creditCard: { produces: "string", generate: creditCard },
  iban: { produces: "string", generate: iban },
  bic: { produces: "string", generate: bic },
  isbn: { produces: "string", generate: isbn },
  ean: { produces: "string", generate: (rng) => withEanDigit(`${rng.between(1, 9)}${rng.digits(11)}`) },

  dateTime: { produces: "string", generate: (rng) => isoDateTime(anyInstant(rng)) },
  date: { produces: "string", generate: (rng) => isoDate(anyInstant(rng)) },
  time: { produces: "string", generate: (rng) => isoDateTime(anyInstant(rng)).slice(11) },
  pastDateTime: { produces: "string", generate: (rng) => isoDateTime(pastInstant(rng)) },
  pastDate: { produces: "string", generate: (rng) => isoDate(pastInstant(rng)) },
  futureDateTime: { produces: "string", generate: (rng) => isoDateTime(futureInstant(rng)) },
  futureDate: { produces: "string", generate: (rng) => isoDate(futureInstant(rng)) },
  epochSeconds: { produces: "integer", generate: (rng) => Math.floor(anyInstant(rng) / 1000) },
  duration: { produces: "string", generate: (rng) => `P${rng.between(1, 30)}DT${rng.between(0, 23)}H` },

  url: { produces: "string", generate: (rng) => `https://${domain(rng)}/${slug(rng)}` },
  domain: { produces: "string", generate: domain },
  // example.com is reserved by the IETF for exactly this: a generated URL pointing at somebody's real CDN is a
  // fixture file that hammers it every time a test suite loads.
  imageUrl: { produces: "string", generate: (rng) => `https://example.com/images/${slug(rng)}.jpg` },
  slug: { produces: "string", generate: slug },
  // RFC 5737's three documentation ranges, for the reason the IPv6 ones are in 2001:db8::/32.
  ipv4: { produces: "string", generate: (rng) => `${rng.pick(DOCUMENTATION_IPV4)}.${rng.between(1, 254)}` },
  ipv6: { produces: "string", generate: ipv6 },
  mac: { produces: "string", generate: mac },
  uuid: { produces: "string", generate: uuid },
  userAgent: { produces: "string", generate: (rng) => rng.pick(USER_AGENTS) },
  httpMethod: { produces: "string", generate: (rng) => rng.pick(HTTP_METHODS) },
  httpStatus: { produces: "integer", generate: (rng) => rng.pick(HTTP_STATUSES) },
  mimeType: { produces: "string", generate: (rng) => rng.pick(MIME_TYPES) },
  fileName: { produces: "string", generate: (rng) => `${slug(rng)}.${rng.pick(FILE_EXTENSIONS)}` },
  semver: { produces: "string", generate: (rng) => `${rng.below(6)}.${rng.below(20)}.${rng.below(40)}` },
  token: { produces: "string", generate: (rng) => token(rng, 32) },
  languageTag: { produces: "string", generate: (rng) => rng.pick(LANGUAGE_TAGS) },
  hostname: { produces: "string", generate: domain },

  word: { produces: "string", generate: (rng) => rng.pick(LOREM) },
  title: { produces: "string", generate: (rng) => capitalise(words(rng, rng.between(2, 6))) },
  sentence: { produces: "string", generate: (rng) => sentence(rng) },
  paragraph: {
    produces: "string",
    generate: (rng) => Array.from({ length: rng.between(3, 6) }, () => sentence(rng)).join(" "),
  },

  boolean: { produces: "boolean", generate: (rng) => rng.chance(0.5) },
  integer: { produces: "integer", generate: (rng) => rng.between(1, 1000) },
  decimal: { produces: "number", generate: (rng) => rng.float(0, 1000, 2) },
  status: { produces: "string", generate: (rng) => rng.pick(STATUSES) },
  colourName: { produces: "string", generate: (rng) => rng.pick(COLOUR_NAMES) },
  hexColour: { produces: "string", generate: (rng) => `#${hex(rng, 6)}` },
} satisfies Record<string, Field>;

// The table is written once and read under two types: `satisfies` on the literal above is what makes `FieldId` the
// list of keys rather than `string`, and the annotation here is what gives every entry the two-argument signature its
// callers pass — several of these take only the stream, and without it a caller could not hand them a locale.
export const FIELDS: Record<FieldId, Field> = TABLE;

// `#` is a digit and `?` an upper-case letter; everything else is written through as it stands, which is what carries
// the spaces and the hyphens a postcode and a phone number are read with.
export function fromPattern(rng: Rng, pattern: string, letters = LETTERS): string {
  let out = "";
  for (const character of pattern) {
    if (character === "#") out += rng.below(10);
    else if (character === "?") out += letters[rng.below(letters.length)];
    else out += character;
  }
  return out;
}

// A number whose Luhn digit holds and whose prefix belongs to a real issuer range, because a test card that a
// payment library rejects for its prefix tests nothing.
function creditCard(rng: Rng): string {
  const brand = rng.pick(CARD_BRANDS);
  const length = rng.pick(brand.lengths);
  const prefix = rng.pick(brand.prefixes);
  const body = prefix + rng.digits(length - prefix.length - 1);
  return body + luhnDigit(body);
}

function iban(rng: Rng, locale: Locale): string {
  // A country with no IBAN at all still has to answer for a column called `iban`, so it borrows the one next door.
  const country = locale.iban ?? FALLBACK_IBAN;
  const bban = fromBbanSpec(rng, IBAN_COUNTRIES[country].bban);
  return `${country}${ibanCheckDigits(country, bban)}${bban}`;
}

// `n18`, `a4n14`: a letter saying which characters and a count saying how many, read pair by pair.
function fromBbanSpec(rng: Rng, spec: string): string {
  let out = "";
  for (const [, kind, count] of spec.matchAll(/([nac])(\d+)/g)) {
    const alphabet = kind === "n" ? DIGITS : kind === "a" ? LETTERS : LETTERS + DIGITS;
    for (let i = 0; i < Number(count); i++) out += alphabet[rng.below(alphabet.length)];
  }
  return out;
}

function bic(rng: Rng, locale: Locale): string {
  const bank = Array.from({ length: 4 }, () => LETTERS[rng.below(LETTERS.length)]).join("");
  const location = fromPattern(rng, "??");
  return `${bank}${locale.countryCode}${location}${rng.chance(0.5) ? fromPattern(rng, "???") : ""}`;
}

// A 979 prefix is a real ISBN prefix and a rarer one, which is exactly the row worth having in a batch.
function isbn(rng: Rng): string {
  return withEanDigit(`${rng.chance(0.85) ? "978" : "979"}${rng.digits(9)}`);
}

function withEanDigit(body: string): string {
  return body + eanDigit(body);
}

function fullName(rng: Rng, locale: Locale): string {
  const given = rng.pick(locale.givenNames);
  const family = rng.pick(locale.surnames);
  // Surname first is the name rather than a way of writing it, and a space between them is not always right either.
  return locale.familyNameFirst ? `${family} ${given}` : `${given} ${family}`;
}

function address(rng: Rng, locale: Locale): string {
  return locale.addressFormat
    .replace("{number}", String(rng.between(1, 240)))
    .replace("{street}", rng.pick(locale.streets))
    .replace("{block}", `${rng.between(1, 5)}-${rng.between(1, 30)}-${rng.between(1, 20)}`);
}

// Whatever of a name can be written into an address bar. A name in kanji leaves nothing behind, so the fallback is
// a word rather than an empty local part — `@example.com` is not an address anybody's parser will take.
function handle(rng: Rng, locale: Locale): string {
  const stem = asciiSlug(`${rng.pick(locale.givenNames)}.${rng.pick(locale.surnames)}`);
  return stem.length > 1 ? stem : `${rng.pick(LOREM)}.${rng.pick(LOREM)}`;
}

// NFD splits an accented letter into a letter and its mark, so stripping the marks leaves Müller as muller rather
// than as mller — which is what a system that cannot hold the umlaut would have done to it anyway.
function asciiSlug(text: string): string {
  const stripped = text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  return stripped.replace(/[^a-z0-9.]+/g, "").replace(/^\.+|\.+$/g, "");
}

// The stem is what tells one host from the next; the suffix is one no registry will ever delegate to anybody.
function domain(rng: Rng): string {
  return `${rng.pick(COMPANY_STEMS).toLowerCase()}.${rng.pick(RESERVED_DOMAINS)}`;
}

function slug(rng: Rng): string {
  return words(rng, rng.between(2, 4)).replace(/ /g, "-");
}

function words(rng: Rng, count: number): string {
  return Array.from({ length: count }, () => rng.pick(LOREM)).join(" ");
}

function sentence(rng: Rng): string {
  return `${capitalise(words(rng, rng.between(6, 14)))}.`;
}

function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

// A v4 UUID drawn from the seeded stream rather than from `crypto`, which is the whole point: a batch nobody can
// reproduce is a batch nobody can talk about. It is not unique and is not claimed to be.
function uuid(rng: Rng): string {
  const digits = hex(rng, 32).split("");
  digits[12] = "4";
  digits[16] = "89ab"[rng.below(4)];
  const joined = digits.join("");
  return `${joined.slice(0, 8)}-${joined.slice(8, 12)}-${joined.slice(12, 16)}-${joined.slice(16, 20)}-${
    joined.slice(20)
  }`;
}

// The documentation range 2001:db8::/32, for the same reason the image URLs are on example.com.
function ipv6(rng: Rng): string {
  return `2001:0db8:${Array.from({ length: 6 }, () => hex(rng, 4)).join(":")}`;
}

// The locally administered bit set and the multicast bit clear, which is what a made-up MAC address is allowed to be.
function mac(rng: Rng): string {
  const first = (rng.below(64) * 4 + 2).toString(16).padStart(2, "0");
  return [first, ...Array.from({ length: 5 }, () => hex(rng, 2))].join(":");
}

function hex(rng: Rng, length: number): string {
  let out = "";
  for (let i = 0; i < length; i++) out += HEX[rng.below(16)];
  return out;
}

function token(rng: Rng, length: number): string {
  let out = "";
  for (let i = 0; i < length; i++) out += TOKEN_ALPHABET[rng.below(TOKEN_ALPHABET.length)];
  return out;
}

function anyInstant(rng: Rng): number {
  return rng.between(ANCHOR - YEAR * 5, ANCHOR + YEAR * 5);
}

function pastInstant(rng: Rng): number {
  return rng.between(ANCHOR - YEAR * 5, ANCHOR);
}

function futureInstant(rng: Rng): number {
  return rng.between(ANCHOR, ANCHOR + YEAR * 5);
}

// Somebody between eighteen and eighty at the anchor, which is who a form asking for a date of birth is filled in by.
function bornAt(rng: Rng): number {
  return rng.between(ANCHOR - YEAR * 80, ANCHOR - YEAR * 18);
}

function isoDateTime(instant: number): string {
  return new Date(instant).toISOString().replace(/\.\d{3}Z$/, "Z");
}

function isoDate(instant: number): string {
  return new Date(instant).toISOString().slice(0, 10);
}

// Nothing here reads the clock. A batch that drew its dates from `Date.now()` would be a different batch every time
// the link was opened, which is the one thing a seed is for; so the dates are a window around a fixed instant, and
// that instant is written here rather than being whenever the page happened to load.
const ANCHOR = Date.UTC(2025, 0, 1);

const YEAR = 365.2425 * 24 * 60 * 60 * 1000;

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

const DIGITS = "0123456789";

const HEX = "0123456789abcdef";

// TEST-NET-1, TEST-NET-2 and TEST-NET-3, each a /24 and so written here as the three octets every address shares.
const DOCUMENTATION_IPV4 = ["192.0.2", "198.51.100", "203.0.113"];

// Base58: the characters that survive being read off a screen and typed back in.
const TOKEN_ALPHABET = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";

// Germany's, for a locale whose country issues no IBAN at all; the check digits make it a real one either way.
const FALLBACK_IBAN = "DE";
